import { cx, slugify } from '@/utils/strings'
import Faq from '@/components/ui/faq'

import RichText from '@/components/ui/rich-text'

import type { Entry } from 'contentful'
import type { TypeSectionFaqsSkeleton } from '@/types/ctf'

import type { DynamicActionProps } from '@/components/ui/dynamic-action'
import DynamicAction from '@/components/ui/dynamic-action'
type SectionFaqsEntry = Entry<TypeSectionFaqsSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

type SectionFaqsProps = SectionFaqsEntry['fields'] & {
  isPrintView?: boolean
}

export default function Faqs({
  actions,
  isPrintView,
  internalName,
  eyebrowText,
  headline,
  body,
  style = 'Text Left',
  faqs,
}: SectionFaqsProps) {
  const faqsChunk1 = faqs?.slice(0, Math.ceil(faqs.length / 2)) || []
  const faqsChunk2 = faqs?.slice(Math.ceil(faqs.length / 2), faqs.length) || []

  return isPrintView ? (
    <div className="border-b py-6">
      {(headline || body) && (
        <>
          {headline && <h2 className="mb-2">{headline}</h2>}
          {body && <RichText body={body as any} />}
        </>
      )}
      {faqs && (
        <ul className="list-disc pl-5">
          {faqs.map((faq) => (
            <li className="break-inside-avoid">
              <h4 className="text-pretty!">{faq?.fields?.question}</h4>
              <RichText body={faq?.fields?.answer as any} />
            </li>
          ))}
        </ul>
      )}
    </div>
  ) : (
    <section className="max-w-8xl px-5 lg:px-9 mx-auto" id={slugify(internalName)}>
      <div
        className={cx(
          (style === 'Text Left' || style === 'Text Right') && 'flex gap-6 flex-col lg:flex-row',
          style === 'Text Right' && 'lg:flex-row-reverse'
        )}
      >
        {(headline || body || eyebrowText) && (
          <div
            className={cx(
              style === 'Text Left' || style === 'Text Right' ? 'lg:w-1/3 text-left' : 'max-w-4xl mx-auto text-center'
            )}
          >
            {eyebrowText && <span className="text-eyebrow uppercase mb-3 block">{eyebrowText}</span>}
            {headline && <h3 className="h2 mb-3">{headline}</h3>}
            {body && <RichText body={body as any} />}
            {actions && (
              <div
                className={cx(
                  'flex flex-wrap gap-6 mb-3 lg:mb-6',
                  (style === 'Single Column' || style === 'Two Column') && 'justify-center'
                )}
              >
                {actions?.map((action) => {
                  if (!action) return null
                  const type = action.sys.contentType.sys.id
                  const fields = {
                    type,
                    ...action.fields,
                  } as DynamicActionProps
                  return (
                    <DynamicAction
                      key={action?.sys.id}
                      id={action?.sys.id}
                      {...fields}
                      size="Smaller"
                      data-testid="card-cta"
                    />
                  )
                })}
              </div>
            )}
          </div>
        )}
        {faqs && (
          <div className={cx(style === 'Text Left' || style === 'Text Right' ? 'lg:w-2/3' : 'mt-6 lg:mt-10')}>
            {style === 'Two Column' ? (
              <div className="grid md:grid-cols-2 md:gap-4">
                <div>
                  {faqsChunk1.map((faq) => (
                    <Faq key={faq?.sys.id} {...faq?.fields} />
                  ))}
                </div>
                <div>
                  {faqsChunk2.map((faq) => (
                    <Faq key={faq?.sys.id} {...faq?.fields} />
                  ))}
                </div>
              </div>
            ) : (
              faqs.map((faq) => <Faq key={faq?.sys.id} {...faq?.fields} />)
            )}
          </div>
        )}
      </div>
    </section>
  )
}
